import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView } from "react-native";
import {
  PreloadedQuery,
  RelayEnvironmentProvider,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import { CardEntryErrorBanner } from "@/features/Square/CardEntryErrorBanner";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import {
  CheckIcon,
  PlusIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { SquareCard } from "@whoosh/features/src/Square";
import { SquareCard as SquareCardType } from "@whoosh/features/src/Square/features/SquareCardEntry/types";

import { PaymentMethod } from "../../../../features/Square/PaymentMethod";
import { SquareCardEntryModal } from "../../../../features/Square/SquareCardEntryModal/SquareCardEntryModal";
import { useCreateUserCardOnFile } from "../../../../features/Square/SquareCardEntryModal/hooks/useCreateUserCardOnFile";
import { InputForUpdateEventSignUp } from "../../../EventDetail/hooks/__generated__/useUpdateEventSignUpMutation.graphql";
import { useUpdateEventSignUp } from "../../../EventDetail/hooks/useUpdateEventSignUp";
import { EventSignupPaymentMethodSelectModal_user$key } from "./__generated__/EventSignupPaymentMethodSelectModal_user.graphql";
import { EventSignupPaymentMethodSelectQuery } from "./__generated__/EventSignupPaymentMethodSelectQuery.graphql";

type Props = {
  active?: boolean;
  eventSignUp: InputForUpdateEventSignUp;
  eventSignUpId: string;
  persistedSelectedCardId: string | null;
  selectedCardId: string | null;
  setSelectedCardId: (cardId: string | null) => void;
  onPaymentCardUpdatePendingChange: (isPending: boolean) => void;
};

export const EventSignupPaymentMethodSelect = ({
  active = true,
  eventSignUp,
  eventSignUpId,
  persistedSelectedCardId,
  selectedCardId,
  setSelectedCardId,
  onPaymentCardUpdatePendingChange,
}: Props) => {
  const [queryRef, loadQuery] =
    useQueryLoader<EventSignupPaymentMethodSelectQuery>(
      EventSignupPaymentMethodSelectQueryString
    );

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  const refreshQuery = useCallback(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  return (
    <Suspense
      fallback={
        <Box
          p={3}
          borderRadius="2"
          backgroundColor="surface-primary"
          borderWidth={4}
          borderColor="surface-secondary"
          gap={2}
        >
          <SkeletonLoader height={20} width={140} borderRadius="2" />
          <SkeletonLoader height={32} width="100%" borderRadius="2" />
        </Box>
      }
    >
      {queryRef ? (
        <EventSignupPaymentMethodSelectInner
          queryRef={queryRef}
          refreshQuery={refreshQuery}
          active={active}
          eventSignUp={eventSignUp}
          eventSignUpId={eventSignUpId}
          persistedSelectedCardId={persistedSelectedCardId}
          selectedCardId={selectedCardId}
          setSelectedCardId={setSelectedCardId}
          onPaymentCardUpdatePendingChange={onPaymentCardUpdatePendingChange}
        />
      ) : null}
    </Suspense>
  );
};

const EventSignupPaymentMethodSelectInner = ({
  queryRef,
  refreshQuery,
  active,
  eventSignUp,
  eventSignUpId,
  persistedSelectedCardId,
  selectedCardId,
  setSelectedCardId,
  onPaymentCardUpdatePendingChange,
}: {
  queryRef: PreloadedQuery<EventSignupPaymentMethodSelectQuery>;
  refreshQuery: () => void;
  active: boolean;
  eventSignUp: InputForUpdateEventSignUp;
  eventSignUpId: string;
  persistedSelectedCardId: string | null;
  selectedCardId: string | null;
  setSelectedCardId: (cardId: string | null) => void;
  onPaymentCardUpdatePendingChange: (isPending: boolean) => void;
}) => {
  const data = usePreloadedQuery(
    EventSignupPaymentMethodSelectQueryString,
    queryRef
  );

  const cardEdges = useMemo(
    () => data.session?.user?.squareCards?.edges ?? [],
    [data.session?.user?.squareCards?.edges]
  );

  const defaultCardId = useMemo(
    () =>
      cardEdges.find((edge) => !!edge?.node?.default)?.node?.id ??
      cardEdges[0]?.node?.id ??
      null,
    [cardEdges]
  );

  const [cardEntryError, setCardEntryError] = useState<string>();
  const [isExistingCardsModalOpen, setIsExistingCardsModalOpen] =
    useState(false);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [isAddCardLoading, setIsAddCardLoading] = useState(false);

  const handleUpdateFailure = useCallback(() => {
    setSelectedCardId(persistedSelectedCardId);
  }, [persistedSelectedCardId, setSelectedCardId]);

  const [updateEventSignUp, isUpdatingEventSignUp] = useUpdateEventSignUp(
    () => {},
    handleUpdateFailure
  );

  useEffect(() => {
    onPaymentCardUpdatePendingChange(isUpdatingEventSignUp);
  }, [isUpdatingEventSignUp, onPaymentCardUpdatePendingChange]);

  useEffect(
    () => () => {
      onPaymentCardUpdatePendingChange(false);
    },
    [onPaymentCardUpdatePendingChange]
  );

  const handleSelectPaymentMethod = useCallback(
    (paymentCardOnFileId: string) => {
      if (paymentCardOnFileId === selectedCardId) {
        return;
      }

      setSelectedCardId(paymentCardOnFileId);
      updateEventSignUp({
        eventSignUpId,
        notifyMember: false,
        eventSignUp: {
          ...eventSignUp,
          paymentCardOnFileId,
        },
      });
    },
    [
      eventSignUp,
      eventSignUpId,
      selectedCardId,
      setSelectedCardId,
      updateEventSignUp,
    ]
  );

  useEffect(() => {
    // Saving a first card updates Relay before Square has dismissed. Leave
    // selection to onCardEntrySaved while entry or recovery is active.
    if (
      !isAddCardModalOpen &&
      !isExistingCardsModalOpen &&
      !cardEntryError &&
      !selectedCardId &&
      defaultCardId
    ) {
      handleSelectPaymentMethod(defaultCardId);
    }
  }, [
    cardEntryError,
    defaultCardId,
    handleSelectPaymentMethod,
    isAddCardModalOpen,
    isExistingCardsModalOpen,
    selectedCardId,
  ]);

  const selectedCardRef = useMemo(
    () =>
      cardEdges.find((edge) => edge?.node?.id === selectedCardId)?.node ?? null,
    [cardEdges, selectedCardId]
  );

  const { runAfterIdle } = useModalTransition();

  const [createSquareCard, isCreatingSquareCard] = useCreateUserCardOnFile();

  const handleAddPress = useCallback(() => {
    setCardEntryError(undefined);
    setIsAddCardModalOpen(true);
  }, []);

  const handleChangePress = useCallback(() => {
    setCardEntryError(undefined);
    if (cardEdges.length > 0) {
      setIsExistingCardsModalOpen(true);
    } else {
      setIsAddCardModalOpen(true);
    }
  }, [cardEdges.length]);

  const handleCardEntryComplete = (squareCard: SquareCardType) =>
    createSquareCard({ squareNonce: squareCard.nonce });

  return (
    <>
      <PaymentMethod
        squareCardRef={selectedCardRef}
        usageDate={null}
        onAddPress={handleAddPress}
        onChangePress={handleChangePress}
        active={active}
        canUpdate={true}
      />

      {data.session?.user ? (
        <EventSignupPaymentMethodSelectModal
          errorMessage={cardEntryError}
          isOpen={isExistingCardsModalOpen}
          setIsOpen={setIsExistingCardsModalOpen}
          fragmentRef={data.session.user}
          selectedPaymentMethodId={selectedCardId}
          setSelectedPaymentMethodId={(id) => {
            setCardEntryError(undefined);
            handleSelectPaymentMethod(id);
            setIsExistingCardsModalOpen(false);
          }}
          openSquareCardEntryModal={() => {
            setCardEntryError(undefined);
            setIsExistingCardsModalOpen(false);
            runAfterIdle(() => {
              setIsAddCardModalOpen(true);
            });
          }}
        />
      ) : null}

      {isAddCardModalOpen ? (
        <SquareCardEntryModal
          isOpen={isAddCardModalOpen}
          onCardEntryCancel={() => {
            setIsAddCardModalOpen(false);
            setIsExistingCardsModalOpen(true);
          }}
          onCardEntryError={(message) => {
            setCardEntryError(message);
            refreshQuery();
            setIsAddCardModalOpen(false);
            setIsExistingCardsModalOpen(true);
          }}
          onCardEntryComplete={handleCardEntryComplete}
          onCardEntrySaved={(cardId) => {
            setIsAddCardModalOpen(false);
            handleSelectPaymentMethod(cardId);
            refreshQuery();
          }}
          loading={isAddCardLoading || isCreatingSquareCard}
          setLoading={setIsAddCardLoading}
        />
      ) : null}
    </>
  );
};

type EventSignupPaymentMethodSelectModalProps = {
  errorMessage?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  fragmentRef: EventSignupPaymentMethodSelectModal_user$key;
  selectedPaymentMethodId: string | null;
  setSelectedPaymentMethodId: (id: string) => void;
  openSquareCardEntryModal: () => void;
};

const EventSignupPaymentMethodSelectModal = (
  props: EventSignupPaymentMethodSelectModalProps
) => {
  const environment = useRelayEnvironment();

  return (
    <StandardModal
      isOpen={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      disableContentScroll={true}
      upperTitle={t`Payment Methods`}
      content={
        <RelayEnvironmentProvider environment={environment}>
          <Box flex={1}>
            <Box px={6}>
              <CardEntryErrorBanner message={props.errorMessage} />
            </Box>
            <EventSignupPaymentMethodSelectModalInner {...props} />
          </Box>
        </RelayEnvironmentProvider>
      }
    />
  );
};

const EventSignupPaymentMethodSelectModalInner = ({
  fragmentRef,
  selectedPaymentMethodId,
  setSelectedPaymentMethodId,
  openSquareCardEntryModal,
}: EventSignupPaymentMethodSelectModalProps) => {
  const data = useFragment(
    EventSignupPaymentMethodSelectModalFragmentString,
    fragmentRef
  );

  return (
    <Box flex={1} px={6} pb={6}>
      <Box my={3}>
        <ScrollView>
          {data?.squareCards?.edges?.map((card, index) => (
            <SquareCard
              key={index}
              fragmentRef={card?.node ?? null}
              onPress={() => {
                if (card?.node?.id) {
                  setSelectedPaymentMethodId(card.node.id);
                }
              }}
              rightIcon={
                selectedPaymentMethodId === card?.node?.id ? (
                  <CheckIcon />
                ) : null
              }
            />
          ))}
        </ScrollView>
      </Box>

      <Pressable
        onPress={openSquareCardEntryModal}
        accessibilityRole="button"
        accessibilityLabel={t`Add New Credit Card`}
      >
        <HStack gap={2} alignItems="center">
          <Box
            bg="surface-secondary"
            width={40}
            height={26}
            borderRadius="2"
            alignItems="center"
            justifyContent="center"
          >
            <PlusIcon width={16} height={16} color="text-main-primary" />
          </Box>
          <Text color="text-main-primary">{t`Add New Credit Card`}</Text>
        </HStack>
      </Pressable>
    </Box>
  );
};

const EventSignupPaymentMethodSelectQueryString = graphql`
  query EventSignupPaymentMethodSelectQuery {
    session {
      user {
        ... on MemberUser {
          ...EventSignupPaymentMethodSelectModal_user
          squareCards(enabled: true) {
            edges {
              node {
                id
                default
                ...PaymentMethod_squareCardOnFile
              }
            }
          }
        }
      }
    }
  }
`;

const EventSignupPaymentMethodSelectModalFragmentString = graphql`
  fragment EventSignupPaymentMethodSelectModal_user on MemberUser {
    squareCards(enabled: true) {
      edges {
        node {
          id
          ...SquareCardFragment
        }
      }
    }
  }
`;
